/**
 * Audio Editor Web Agent Bridge
 *
 * Republishes a curated subset of the editor's existing in-page automation
 * surface (`window.trebble.globalHelpers.audioEditorAutomation`, see
 * js/models/audioEditor/EDITOR_AUTOMATION_API.md) as typed, MCP-shaped tools
 * for browser-resident agents, via the shared Web Agent Tool Bridge
 * (WebMCP `document.modelContext` + `window.__TREBBLE_AGENT_TOOLS__`).
 *
 * Every tool resolves the ACTIVE editor at call time and delegates to the same
 * automation methods Playwright and console automation already use — no new
 * mutation paths. SequencerController calls registerAudioEditorWebAgentBridge()
 * when an editor registers its automation API and
 * releaseAudioEditorWebAgentBridge() when one unregisters; the tools are
 * withdrawn when no editor remains.
 *
 * TRE-1205 — containment: the mutating tools are gated behind
 * WEB_AGENT_EDITOR_WRITE_TOOLS, which fails closed — every production-mode build is OFF
 * unless it opts in explicitly (Amplify previews and the seeded E2E lane do). Read-only
 * tools always publish, so agents can still inspect a project on production.
 *
 * The gate closes both exposed surfaces: this bridge withholds mutating tool definitions,
 * while SequencerController strips mutating methods from the global registry and per-editor
 * API. This is containment, not XSS remediation — page JS can still synthesize UI behavior
 * or reach other authenticated code — but it removes the direct global mutation handles and
 * keeps write tools out of browser-resident agents' `document.modelContext` menus.
 *
 * The gate is evaluated once at registration. See docs/WEBMCP_EDITOR_TOOL_INVENTORY.md for
 * the per-tool classification and the full residual-risk note.
 */

import {
    getWebAgentToolBridge,
    type WebAgentToolDefinition,
} from '../../../components/timeloom/agent/webAgentToolBridge';

interface AutomationEditorApi {
    getMetadata(): unknown;
    getHistoryState(): unknown;
    getSelection(): unknown;
    getTranscriptSnapshot(): unknown;
    findText(queryText: string, options?: Record<string, unknown>): unknown;
    getNode(cid: string): unknown;
    getPlaybackDebugState(): { playbackPositionMs?: unknown } | null;
    getVideoPreviewState(): unknown;
    selectText(text: string, options?: Record<string, unknown>): unknown;
    selectVisibleWordRange(startWordIndex: number, endWordIndex: number): unknown;
    deleteSelection(): unknown;
    copySelection(): unknown;
    cutSelection(): unknown;
    pasteClipboardBeforeSelection(): unknown;
    undo(): unknown;
    redo(): unknown;
    saveProject(): unknown;
    seekVideoPreviewToTimeMs(timeMs: number): unknown;
}

interface AutomationRegistry {
    listEditors(): readonly unknown[];
    getActiveEditorId(): unknown;
    getActiveEditor(): AutomationEditorApi | null;
}

interface TrebbleGlobalWindow {
    trebble?: {
        globalHelpers?: {
            audioEditorAutomation?: AutomationRegistry;
        };
    };
}

function getAutomationRegistry(): AutomationRegistry | null {
    if (typeof window === 'undefined') return null;
    return (window as unknown as TrebbleGlobalWindow).trebble?.globalHelpers?.audioEditorAutomation ?? null;
}

function requireAutomationRegistry(): AutomationRegistry {
    const registry = getAutomationRegistry();
    if (!registry) {
        throw new Error('No active Trebble editor. Open a project in the Trebble editor first.');
    }
    return registry;
}

function requireActiveEditor(): AutomationEditorApi {
    const editor = requireAutomationRegistry().getActiveEditor();
    if (!editor) {
        throw new Error('No active Trebble editor. Open a project in the Trebble editor first.');
    }
    return editor;
}

function requireString(input: Record<string, unknown>, key: string): string {
    const value = input[key];
    if (typeof value !== 'string' || value.trim() === '') {
        throw new Error(`"${key}" is required and must be a non-empty string.`);
    }
    return value;
}

function requireNumber(input: Record<string, unknown>, key: string): number {
    const value = input[key];
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw new Error(`"${key}" is required and must be a finite number.`);
    }
    return value;
}

function requireInteger(input: Record<string, unknown>, key: string, minimum: number): number {
    const value = requireNumber(input, key);
    if (!Number.isInteger(value) || value < minimum) {
        throw new Error(`"${key}" must be an integer greater than or equal to ${minimum}.`);
    }
    return value;
}

function requireNonNegativeNumber(input: Record<string, unknown>, key: string): number {
    const value = requireNumber(input, key);
    if (value < 0) {
        throw new Error(`"${key}" must be greater than or equal to 0.`);
    }
    return value;
}

const READ_ANNOTATIONS = { readOnlyHint: true, destructiveHint: false, idempotentHint: true, openWorldHint: false };
const WRITE_ANNOTATIONS = { readOnlyHint: false, destructiveHint: false, idempotentHint: false, openWorldHint: false };

const EMPTY_INPUT_SCHEMA: Record<string, unknown> = { type: 'object', properties: {}, additionalProperties: false };

function buildEditorTools(): WebAgentToolDefinition[] {
    return [
        {
            name: 'editor.get_context',
            title: 'Get editor context',
            description: 'Read the active Trebble editor context: editor id, project metadata, undo/redo history state, '
                + 'and the current transcript selection. Call this first to confirm a project is open.',
            inputSchema: EMPTY_INPUT_SCHEMA,
            annotations: READ_ANNOTATIONS,
            execute: async () => {
                const registry = requireAutomationRegistry();
                const editor = requireActiveEditor();
                return {
                    activeEditorId: registry.getActiveEditorId(),
                    editors: registry.listEditors(),
                    metadata: editor.getMetadata(),
                    historyState: editor.getHistoryState(),
                    selection: editor.getSelection(),
                };
            },
        },
        {
            name: 'editor.get_transcript',
            title: 'Get transcript snapshot',
            description: 'Read the full visible transcript of the open project: word list with per-word cid, text, and '
                + 'paragraph index, plus the current selection. Word cids anchor selection and editing tools. '
                + 'Large projects return large snapshots; prefer editor.find_text when locating a phrase.',
            inputSchema: EMPTY_INPUT_SCHEMA,
            annotations: READ_ANNOTATIONS,
            execute: async () => requireActiveEditor().getTranscriptSnapshot(),
        },
        {
            name: 'editor.find_text',
            title: 'Find text in transcript',
            description: 'Find a phrase in the visible transcript (case/punctuation-insensitive). Returns the matched '
                + 'word range with startCid/endCid and word indexes for selection tools.',
            inputSchema: {
                type: 'object',
                properties: {
                    queryText: { type: 'string', description: 'The phrase to find in the transcript.' },
                    occurrence: { type: 'integer', minimum: 1, description: '1-based occurrence when the phrase appears multiple times.' },
                },
                required: ['queryText'],
                additionalProperties: false,
            },
            annotations: READ_ANNOTATIONS,
            execute: async (input) => {
                const editor = requireActiveEditor();
                const queryText = requireString(input, 'queryText');
                const occurrence = input.occurrence === undefined
                    ? undefined
                    : { occurrence: requireInteger(input, 'occurrence', 1) };
                return editor.findText(queryText, occurrence);
            },
        },
        {
            name: 'editor.get_selection',
            title: 'Get current selection',
            description: 'Read the current transcript selection snapshot of the open project.',
            inputSchema: EMPTY_INPUT_SCHEMA,
            annotations: READ_ANNOTATIONS,
            execute: async () => requireActiveEditor().getSelection(),
        },
        {
            name: 'editor.get_node',
            title: 'Get node metadata',
            description: 'Read metadata for one sequencer node by cid: type, text, selection and wrap linkage.',
            inputSchema: {
                type: 'object',
                properties: { cid: { type: 'string', description: 'Sequencer node cid from a transcript snapshot or find result.' } },
                required: ['cid'],
                additionalProperties: false,
            },
            annotations: READ_ANNOTATIONS,
            execute: async (input) => requireActiveEditor().getNode(requireString(input, 'cid')),
        },
        {
            name: 'editor.get_playback_state',
            title: 'Get playback state',
            description: 'Read the current preview playback position (ms) and video preview state.',
            inputSchema: EMPTY_INPUT_SCHEMA,
            annotations: READ_ANNOTATIONS,
            execute: async () => {
                const editor = requireActiveEditor();
                const playbackDebugState = editor.getPlaybackDebugState();
                return {
                    playbackPositionMs: playbackDebugState?.playbackPositionMs ?? null,
                    videoPreviewState: editor.getVideoPreviewState(),
                };
            },
        },
        {
            name: 'editor.select_text',
            title: 'Select text',
            description: 'Select a phrase in the transcript (like a user selecting words). The transcript view and '
                + 'canvas update live. Returns the resulting selection snapshot.',
            inputSchema: {
                type: 'object',
                properties: {
                    text: { type: 'string', description: 'The phrase to select.' },
                    occurrence: { type: 'integer', minimum: 1, description: '1-based occurrence when the phrase appears multiple times.' },
                },
                required: ['text'],
                additionalProperties: false,
            },
            annotations: WRITE_ANNOTATIONS,
            execute: async (input) => {
                const editor = requireActiveEditor();
                const text = requireString(input, 'text');
                const occurrence = input.occurrence === undefined
                    ? undefined
                    : { occurrence: requireInteger(input, 'occurrence', 1) };
                return editor.selectText(text, occurrence);
            },
        },
        {
            name: 'editor.select_word_range',
            title: 'Select word range',
            description: 'Select a visible word range by 0-based word indexes from editor.get_transcript.',
            inputSchema: {
                type: 'object',
                properties: {
                    startWordIndex: { type: 'integer', minimum: 0 },
                    endWordIndex: { type: 'integer', minimum: 0 },
                },
                required: ['startWordIndex', 'endWordIndex'],
                additionalProperties: false,
            },
            annotations: WRITE_ANNOTATIONS,
            execute: async (input) => requireActiveEditor().selectVisibleWordRange(
                requireInteger(input, 'startWordIndex', 0),
                requireInteger(input, 'endWordIndex', 0),
            ),
        },
        {
            name: 'editor.delete_selection',
            title: 'Delete selection',
            description: 'Delete the currently selected transcript content, exactly like the editor delete action. '
                + 'Undoable with editor.undo. Returns post-delete transcript/selection/history snapshots.',
            inputSchema: EMPTY_INPUT_SCHEMA,
            annotations: { ...WRITE_ANNOTATIONS, destructiveHint: true },
            execute: async () => requireActiveEditor().deleteSelection(),
        },
        {
            name: 'editor.copy_selection',
            title: 'Copy selection',
            description: 'Copy the current selection to the editor clipboard.',
            inputSchema: EMPTY_INPUT_SCHEMA,
            annotations: WRITE_ANNOTATIONS,
            execute: async () => requireActiveEditor().copySelection(),
        },
        {
            name: 'editor.cut_selection',
            title: 'Cut selection',
            description: 'Cut the current selection to the editor clipboard. Undoable with editor.undo.',
            inputSchema: EMPTY_INPUT_SCHEMA,
            annotations: { ...WRITE_ANNOTATIONS, destructiveHint: true },
            execute: async () => requireActiveEditor().cutSelection(),
        },
        {
            name: 'editor.paste_before_selection',
            title: 'Paste before selection',
            description: 'Paste the editor clipboard content before the current selection. Undoable with editor.undo.',
            inputSchema: EMPTY_INPUT_SCHEMA,
            annotations: WRITE_ANNOTATIONS,
            execute: async () => requireActiveEditor().pasteClipboardBeforeSelection(),
        },
        {
            name: 'editor.undo',
            title: 'Undo',
            description: 'Undo the last editor operation through the live editor history stack.',
            inputSchema: EMPTY_INPUT_SCHEMA,
            annotations: WRITE_ANNOTATIONS,
            execute: async () => requireActiveEditor().undo(),
        },
        {
            name: 'editor.redo',
            title: 'Redo',
            description: 'Redo the last undone editor operation.',
            inputSchema: EMPTY_INPUT_SCHEMA,
            annotations: WRITE_ANNOTATIONS,
            execute: async () => requireActiveEditor().redo(),
        },
        {
            name: 'editor.save_project',
            title: 'Save project',
            description: 'Save the open project (one new project version), exactly like the editor save action.',
            inputSchema: EMPTY_INPUT_SCHEMA,
            annotations: WRITE_ANNOTATIONS,
            execute: async () => requireActiveEditor().saveProject(),
        },
        {
            name: 'editor.seek_preview',
            title: 'Seek video preview',
            description: 'Seek the video preview to a time in milliseconds so the canvas shows that frame. Combine with '
                + 'a screenshot of the page to visually verify edits.',
            inputSchema: {
                type: 'object',
                properties: { timeMs: { type: 'number', minimum: 0, description: 'Target preview time in milliseconds.' } },
                required: ['timeMs'],
                additionalProperties: false,
            },
            annotations: WRITE_ANNOTATIONS,
            execute: async (input) => requireActiveEditor().seekVideoPreviewToTimeMs(
                requireNonNegativeNumber(input, 'timeMs'),
            ),
        },
    ];
}

/**
 * Whether mutating editor automation may be exposed on this build.
 *
 * This is the single definition of the gate. SequencerController imports it to decide
 * which methods `_createAutomationApi()` returns, so the raw automation registry and this
 * MCP republication of it are always gated together — one flag, not two.
 *
 * webpack's DefinePlugin replaces the expression at build time (see webpack.config.js):
 * production-mode builds get '' unless they opt in explicitly. Under Node — unit tests, and
 * the dev-mode builds the E2E lanes serve — it stays a real environment variable, so the
 * gate can be exercised from both sides.
 */
export function areEditorWriteToolsEnabled(): boolean {
    return process.env.WEB_AGENT_EDITOR_WRITE_TOOLS === 'true';
}

/**
 * The gate follows each tool's own `readOnlyHint` annotation rather than a second,
 * hand-maintained list — a new tool is contained by default unless it declares itself
 * read-only. `annotations` is optional on WebAgentToolDefinition, so a tool that omits
 * it is treated as mutating.
 */
function isReadOnlyTool(tool: WebAgentToolDefinition): boolean {
    return tool.annotations?.readOnlyHint === true;
}

let registeredToolNames: string[] = [];

export function registerAudioEditorWebAgentBridge(): void {
    if (registeredToolNames.length > 0) return;
    const allTools = buildEditorTools();
    const tools = areEditorWriteToolsEnabled() ? allTools : allTools.filter(isReadOnlyTool);
    getWebAgentToolBridge().registerTools(tools);
    registeredToolNames = tools.map(tool => tool.name);
}

export function releaseAudioEditorWebAgentBridge(): void {
    if (registeredToolNames.length === 0) return;
    const registry = getAutomationRegistry();
    if (registry && registry.listEditors().length > 0) return;
    getWebAgentToolBridge().unregisterTools(registeredToolNames);
    registeredToolNames = [];
}

export function getRegisteredAudioEditorWebAgentToolNamesForTests(): string[] {
    return registeredToolNames.slice();
}
